import type { ClassLessonAlarmOffset, ClassLessonAlarmSoundKind } from './classLessonAlarmSettings';
import { getCachedSoundBlob, putCachedSoundBlob } from './classLessonAlarmSoundCache';

let currentlyPlayingAudios: HTMLAudioElement[] = [];

/** 파일명 → object URL (IndexedDB Blob 기반, 같은 디바이스 재사용) */
const objectUrlByFile: Record<string, string> = {};

/** 사운드 파일 교체 시 브라우저·영구 캐시 무효화 */
const CLASS_LESSON_ALARM_SOUND_ASSET_VERSION = '20260711b';

/** 기본 알람 → ALRAM_CLASS_*, 활동 알람 → ALRAM_ACTIVITY_* */
function getClassLessonAlarmSoundFile(
  offset: ClassLessonAlarmOffset,
  soundKind: ClassLessonAlarmSoundKind,
  minutesBefore: number
): string {
  const prefix = soundKind === 'activity' ? 'ALRAM_ACTIVITY' : 'ALRAM_CLASS';

  let file = `${prefix}_END.mp3`;
  if (offset === '1M') {
    file = `${prefix}_END.mp3`;
  } else if (offset === '3M') {
    if (minutesBefore === 3) file = `${prefix}_START.mp3`;
    else if (minutesBefore === 2) file = `${prefix}_2.mp3`;
    else if (minutesBefore === 1) file = `${prefix}_END.mp3`;
  } else if (offset === '5M') {
    if (minutesBefore === 5) file = `${prefix}_START.mp3`;
    else if (minutesBefore === 4) file = `${prefix}_4.mp3`;
    else if (minutesBefore === 3) file = `${prefix}_3.mp3`;
    else if (minutesBefore === 2) file = `${prefix}_2.mp3`;
    else if (minutesBefore === 1) file = `${prefix}_END.mp3`;
  }
  return file;
}

export function getClassLessonAlarmSoundPath(
  offset: ClassLessonAlarmOffset,
  soundKind: ClassLessonAlarmSoundKind,
  minutesBefore: number
): string {
  const file = getClassLessonAlarmSoundFile(offset, soundKind, minutesBefore);
  return `/sounds/${file}?v=${CLASS_LESSON_ALARM_SOUND_ASSET_VERSION}`;
}

/** 파일 1개를 IndexedDB에 확보하고 재생용 object URL을 준비 (네트워크 실패 시 무시) */
async function ensureSoundFileCached(file: string): Promise<void> {
  const cacheKey = `${CLASS_LESSON_ALARM_SOUND_ASSET_VERSION}_${file}`;
  if (objectUrlByFile[file]) return;

  const cached = await getCachedSoundBlob(cacheKey);
  if (cached) {
    objectUrlByFile[file] = URL.createObjectURL(cached);
    return;
  }

  try {
    const res = await fetch(`/sounds/${file}?v=${CLASS_LESSON_ALARM_SOUND_ASSET_VERSION}`);
    if (!res.ok) return;
    const blob = await res.blob();
    await putCachedSoundBlob(cacheKey, blob);
    objectUrlByFile[file] = URL.createObjectURL(blob);
  } catch {
    /* 오프라인·차단 등은 무시 — 재생 시 네트워크 경로로 폴백 */
  }
}

export async function preloadClassLessonAlarmSounds(): Promise<void> {
  const offsets: ClassLessonAlarmOffset[] = ['1M', '3M', '5M'];
  const kinds: ClassLessonAlarmSoundKind[] = ['basic', 'activity'];

  const files = new Set<string>();
  for (const offset of offsets) {
    for (const soundKind of kinds) {
      let minutesList: number[] = [];
      if (offset === '1M') minutesList = [1];
      else if (offset === '3M') minutesList = [3, 2, 1];
      else minutesList = [5, 4, 3, 2, 1];
      for (const minutesBefore of minutesList) {
        files.add(getClassLessonAlarmSoundFile(offset, soundKind, minutesBefore));
      }
    }
  }

  await Promise.all(Array.from(files).map((file) => ensureSoundFileCached(file)));
}

export function playClassLessonAlarmSound(
  offset: ClassLessonAlarmOffset,
  soundKind: ClassLessonAlarmSoundKind,
  minutesBefore: number
): HTMLAudioElement {
  const file = getClassLessonAlarmSoundFile(offset, soundKind, minutesBefore);
  /** 영구 캐시된 object URL 우선, 없으면 네트워크 경로로 폴백 */
  const src =
    objectUrlByFile[file] || `/sounds/${file}?v=${CLASS_LESSON_ALARM_SOUND_ASSET_VERSION}`;
  const audio = new Audio(src);
  currentlyPlayingAudios.push(audio);
  audio.addEventListener('ended', () => {
    const index = currentlyPlayingAudios.indexOf(audio);
    if (index > -1) currentlyPlayingAudios.splice(index, 1);
  });
  // 캐시가 아직 없으면 백그라운드로 확보 (다음 재생부터 즉시·오프라인 가능)
  if (!objectUrlByFile[file]) void ensureSoundFileCached(file);
  return audio;
}

export function stopAllClassLessonAlarmSounds(): void {
  currentlyPlayingAudios.forEach((audio) => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  });
  currentlyPlayingAudios = [];
}

/** 분 경계 후 이 초까지는 정시 울림으로 본다. 넘으면 해당 구간 소리는 생략(게이지만 표시) */
const CLASS_LESSON_ALARM_ON_TIME_TOLERANCE_SEC = 3;

/** 분 경계(구간 시작)를 놓치고 늦게 확인된 울림인지 */
export function isClassLessonAlarmTriggerLate(now: Date = new Date()): boolean {
  return now.getSeconds() > CLASS_LESSON_ALARM_ON_TIME_TOLERANCE_SEC;
}

/** 오늘 교시 시작 시각(HH:mm)의 ms */
export function classLessonAlarmStartsAtMs(targetTimeHHMM: string, now: Date = new Date()): number {
  const [h, m] = targetTimeHHMM.split(':').map(Number);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h || 0, m || 0, 0, 0).getTime();
}

/**
 * 게이지 0 기준 시각.
 * 1분 전 음원은 정확히 1분 길이 → 늦게 잡혀도 지금부터 1분으로 진행해 소리와 게이지 종료를 맞춘다.
 */
export function classLessonAlarmGaugeEndsAtMs(
  targetTimeHHMM: string,
  minutesBefore: number,
  now: Date = new Date()
): number {
  if (minutesBefore === 1 && isClassLessonAlarmTriggerLate(now)) return now.getTime() + 60 * 1000;
  return classLessonAlarmStartsAtMs(targetTimeHHMM, now);
}

/**
 * 목표 시각까지 남은 분(현재 분 기준)이 offset 구간에 포함되면 반환.
 * 초 0에만 판단하면 백그라운드 탭(타이머 지연·1분 정렬)에서 울림을 놓치므로 분 전체에서 판단하고,
 * 같은 분 중복 울림은 호출 측 triggerKey로 막는다.
 */
export function getMinutesBeforeClassLessonAlarm(
  targetTimeHHMM: string,
  offset: ClassLessonAlarmOffset,
  now: Date = new Date()
): number | null {
  const currentTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    0,
    0
  );
  const [hours, minutes] = targetTimeHHMM.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const alarmTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0
  );
  if (alarmTime.getTime() < currentTime.getTime()) {
    alarmTime.setDate(alarmTime.getDate() + 1);
  }

  const diffMinutes = Math.floor((alarmTime.getTime() - currentTime.getTime()) / (60 * 1000));
  let checkMinutes: number[] = [];
  if (offset === '1M') checkMinutes = [1];
  else if (offset === '3M') checkMinutes = [3, 2, 1];
  else checkMinutes = [5, 4, 3, 2, 1];

  return checkMinutes.includes(diffMinutes) ? diffMinutes : null;
}
